'use client';

/**
 * FileInput — the shared primitive for a file picker.
 *
 * Every existing file-upload UI in the app hand-rolls a hidden native
 * `<input type="file">` behind a styled `<label>` (tenant-documents-section,
 * IdentityVerificationV2, document-upload, id-upload, …) — tracked debt per
 * `docs/ui-design-system.md` §6 ("hidden file inputs … remain documented
 * debt"). `src/native-control-ratchet.test.ts` allows that debt to shrink
 * but never grow in a NEW file, so new file-upload UI wraps the native
 * input here instead of adding another one-off. `components/ui/*` is
 * excluded from the scan — this is where a native control is allowed to
 * live.
 *
 * The caller owns the visible label content (icon, "Choose PDF" vs the
 * selected filename, a spinner while uploading) — this component only
 * wires the native input, the `sr-only` + `<label htmlFor>` pattern, and
 * disabled/aria passthrough.
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

export interface FileInputProps {
  id: string;
  accept?: string;
  onFileSelected: (file: File | null) => void;
  /** Visible content of the clickable label (icon + text). */
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

export function FileInput({
  id,
  accept,
  onFileSelected,
  children,
  disabled,
  className,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: FileInputProps) {
  return (
    <div>
      <input
        id={id}
        type="file"
        accept={accept}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        className="sr-only"
        onChange={(e) => {
          onFileSelected(e.target.files?.[0] ?? null);
          // Allow re-selecting the same file (e.g. after a failed upload).
          e.target.value = '';
        }}
      />
      <label
        htmlFor={id}
        aria-disabled={disabled}
        className={cn(
          'inline-flex min-h-9 cursor-pointer items-center gap-1.5 rounded-control border border-input bg-card px-3 text-sm hover:bg-accent',
          disabled && 'pointer-events-none opacity-50',
          className,
        )}
      >
        {children}
      </label>
    </div>
  );
}
